import { IconName } from '@fortawesome/fontawesome-common-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { uniqueId } from 'lodash'
import React, { FC, useState } from 'react'
import { replaceAndStrip } from '../../../utils/string'
import SuggestionChips from './SuggestionChips'

type TargetItem = {
  value: string
  label: string
  color: string
  icon: IconName
}

type Suggestion = {
  values: string[]
  legendBackground?: string
}

type KeywordConditionProps = {
  targetItem: TargetItem
  words: string[]
  operator: string
  onWordsChange: (words: string[]) => void
  onOperatorChange: (operator: string) => void
  suggestion?: Suggestion
}

const KeywordCondition: FC<KeywordConditionProps> = (props) => {
  const {
    targetItem,
    words,
    operator,
    onWordsChange,
    onOperatorChange,
    suggestion,
  } = props

  const [keywordText, setKeywordText] = useState<string>("")

  const addKeyword = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing || e.key !== "Enter") return

    const trimValue = replaceAndStrip(keywordText)
    if (!trimValue || words.includes(trimValue)) return

    onWordsChange([...words, trimValue])
    setKeywordText("")
  }

  const blurKeyword = (keyword: string) => {
    setKeywordText("")
    const trimValue = replaceAndStrip(keyword)
    if (!trimValue || words.includes(trimValue)) return
    onWordsChange([...words, trimValue])
  }

  return (
    <>
      <div className="condition_header">
        <div className="condition_label">
          <FontAwesomeIcon
            icon={['fas', targetItem.icon as IconName]}
            style={{ color: targetItem.color }}
          />
          {targetItem.label}
        </div>
        <div className="match_select">
          <select
            key={`pattern_keyword_${operator}`}
            onChange={(e) => onOperatorChange(e.currentTarget.value)}
            defaultValue={operator}
          >
            <option value="or">
              いずれかを含む
            </option>
            <option value="and">
              全てを含む
            </option>
          </select>
          <div className="caret">
            <FontAwesomeIcon icon={['fas', 'caret-down']} />
          </div>
        </div>
      </div>
      <div>
        <div className="detail_box">
          {
            words.map((keyword) => (
              <div className="chip" key={uniqueId()}>
                {keyword}
                <span className="chip_remove" onClick={() => onWordsChange(words.filter((w) => w !== keyword))} role="none">
                  <FontAwesomeIcon icon={['fas', 'times']} />
                </span>
              </div>
            ))
          }
          <input
            className="keyword_input"
            type="text"
            value={keywordText}
            onChange={(e) => setKeywordText(e.target.value)}
            onKeyDown={(e) => addKeyword(e)}
            onBlur={(e) => blurKeyword(e.target.value)}
            placeholder={words.length ? "" : "キーワードを入力してエンターキーを押してください"}
          />
        </div>
      </div>
      <div>
        <div className="note_list">
          <div className="note">
            {`※ 指定キーワードの${operator === "or" ? "いずれか" : "全て"}が含まれている場合、自動仕分けされます。`}
          </div>
          <div className="note">
            ※ スペースや記号についても、含んだ状態のまま設定されます。ご注意ください。
          </div>
        </div>
      </div>
      {
        !!suggestion?.values.length && (
          <SuggestionChips
            values={suggestion.values}
            selectedValues={words}
            onToggle={(v) => onWordsChange(
              words.includes(v) ? words.filter((w) => w !== v) : [...words, v],
            )}
            legendBackground={suggestion.legendBackground}
          />
        )
      }
    </>
  )
}

export default KeywordCondition
